//@ts-strict-ignore
import React, { memo, ReactNode, useCallback, useContext, useEffect } from 'react';
import ConnectedField from './ConnectedField';
import { areEqualProps } from './utils/shallowCompare';
import * as DataEntities from '../../graphql/entities';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import TabContext from '../Context/TabContext';
import { useDebouncedTemporarySave } from './utils/handleTemporarySave';
import { useWorkspace } from '../../graphql/hooks/useWorkspace';
import { EntityType } from '../../@types/entityTypes';
import { DocumentNode } from 'graphql';
import { IEnumMeta } from '../../graphql/schema/fieldMetaData';
import { Maybe } from '../../graphql/schema/p4types';

const useFieldMeta = (dataid?: string) => {
  const connectedForm = useContext(ConnectedFormContext);

  if (!dataid) return {};

  const strippedDataId = dataid.replace(/\d+./g, ''); //Remove array index's from the dataid.
  return connectedForm.fieldValidation?.[strippedDataId as any] || {};
};

const useDataEntityProps = (props: FieldProps) => {
  const { labelKey, valueKey, query, dataid } = props;
  const fieldMeta = useFieldMeta(dataid);

  if (props.component.displayName !== 'Dropdown') return null;

  if (fieldMeta && fieldMeta.objectType) {
    if (props.options) {
      return {
        options: props.options,
        enum: props.enum,
      };
    }
    if (fieldMeta.enumValues) {
      const sortEnums = (a: IEnumMeta<any>, b: IEnumMeta<any>) => {
        const nameA = a.label.toUpperCase(); // ignore upper and lowercase
        const nameB = b.label.toUpperCase(); // ignore upper and lowercase
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }

        // names must be equal
        return 0;
      };

      return { options: fieldMeta.enumValues.sort(sortEnums), enum: true };
    }
    const dataEntity = DataEntities[fieldMeta.objectType as keyof typeof DataEntities];
    if (dataEntity) {
      if (dataEntity.dropdown) {
        return {
          ...dataEntity.dropdown,
          // Use predefinedFilters from props as priority, to allow it to override where appropriate.
          // (If the defaults are also needed, they should be merged into the prop that's passed through).
          predefinedFilters: props.predefinedFilters ?? dataEntity.dropdown?.predefinedFilters ?? undefined,
          query: props.query || dataEntity.dropdown.query,
        };
      }
      console.warn(
        `Incorrect Configuration: Dropdown property missing for entity
              ${fieldMeta.objectType}`
      );
      return {
        configError: true,
        configErrorMessage: 'Dropdown property missing',
      };
    }
    //Check for data entity from Props.
    if (labelKey && valueKey && query) {
      return {
        labelKey: labelKey,
        valueKey: valueKey,
        query: query,
      };
    }
    console.warn(`Missing Data Entity Configuration: ${fieldMeta.objectType}`);
    return {
      configError: true,
      configErrorMessage: 'Data entity configuration missing',
    };
  }
  return null;
};

const WorkspaceConnectedField = (props: any) => {
  const workspaceProps = useWorkspace<any>(
    props,
    { ...(props?.entity || {}), workspace: props.defaultQuery } as EntityType,
    {
      columns: props.columns || [],
      initialSortColumn: props.initialSortColumn || 'id',
      customColumns: props.customColumns,
    }
  );
  return <ConnectedField {...workspaceProps} {...props} />;
};

//TODO
// - Probably better to serve 'Workspace Connected' via a separate component/wrapper as it's only a handful of fields that require the extra logic.

//May differ slightly depending on the component - Add additional potential types here as appropriate.
export type IFieldOnChange<ValueType = any> = (event: any, newValue: ValueType, oldValue: ValueType) => any;

//By default, allows any number of additional props with 'any' as the type - Ideally should pass the prop types of the underlying component instead
export type FieldProps<IFieldProps = any, ValueType = any> = Omit<
  IFieldProps,
  'value' | 'onChange' | 'onBlur' | 'data-testid' | 'rows'
> & {
  dataid: string;
  datacomponent?: string; //Optional additional string used to create a more unique 'id' field on child component (see createFieldProps).
  component: any;
  label?: Maybe<string>;
  onBlur?: (ev: any, value: any) => any;
  onChange?: IFieldOnChange<ValueType>;
  onFocus?: () => any;
  forwardRef?: boolean;
  type?: string;
  dynamicOptions?: boolean;
  autoFocus?: boolean;
  normalize?: (dataid: string, value: any, previousValues: any) => any;
  debounceMS?: number;
  defaultQuery?: any;
  temporary?: boolean; //Should a temporary save be triggered on BLUR.
  temporaryOnChange?: boolean; //Should a temporary save be triggered on CHANGE.
  listData?: Array<any>; //Can be removed if/when we separate 'WorkspaceConnectedField' out from this component
  mandatory?: boolean;
  forceUpdate?: boolean; //Always re-render (Effectively skips attempt to check if props have changed)
  readOnly?: boolean;
  hideWhenEmpty?: boolean;
  writeDataid?: string; //Used in connectedField to instruct a field to write to a different path than it reads from (E.g. 'displayProfitMargin'/'forceProfitMargin').
  tabIndex?: number;
  additionalVariables?: any; //Used in connectedField to add extra options to queried data.
  busyOnTemporary?: boolean;
  forceReadOnly?: boolean;
  reformatLabel?: boolean;
  query?: DocumentNode; //Used in 'useDataEntityProps' to get options for Dropdowns in some cases.
  placeholder?: string;
  rawLabel?: boolean;
  hint?: ReactNode;
};

const Field = <IFieldProps, ValueType = any>(props: FieldProps<IFieldProps, ValueType>) => {
  const { dataid, normalize } = props;

  const tab = useContext(TabContext);
  const connectedForm = useContext(ConnectedFormContext);
  const dataEntityProps = useDataEntityProps(props);
  const temporarySave = useDebouncedTemporarySave(false, props.debounceMS);

  useEffect(() => {
    connectedForm.register(dataid, props.type, tab.tabId);
    return () => {
      connectedForm.unregister(dataid);
    };
  }, []);

  const callNormalize = useCallback(
    (dataid, value) => {
      if (!normalize) {
        return value;
      }
      const previousValues = connectedForm.getValues?.();
      return normalize(dataid, value, previousValues);
    },
    [connectedForm]
  );

  //If defaultQuery provided AND we don't already have listData, use WorkspaceConnectedField (SalesReceiptForm handles the workspace query so that it can access correct filters).
  const InnerConnectedField = props.defaultQuery && !props.listData ? WorkspaceConnectedField : ConnectedField;

  const { validationRules } = useFieldMeta(props.dataid);

  return (
    <InnerConnectedField
      {...props}
      {...dataEntityProps}
      connectedForm={connectedForm}
      normalize={callNormalize}
      mandatory={props.mandatory || validationRules?.mandatory}
      temporarySave={temporarySave}
    />
  );
};

export default memo(Field, (prev: any, next: any) => !next.forceUpdate && areEqualProps(prev, next)) as typeof Field;
